import { Component, ComponentType, lazy, ReactNode } from "react";
import i18next from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

type Props = {
    children: ReactNode;
    onError?: () => void;
    fallback?: ReactNode;
    /**
     * Clearing the caught error when this changes lets a shared boundary recover without a reload.
     */
    resetKey?: string;
};

type State = {
    error: unknown;
    resetKey: string | undefined;
};

export class ChunkLoadError extends Error {
    constructor(cause: unknown) {
        super("Failed to load a code-split chunk", { cause });
        this.name = "ChunkLoadError";
    }
}

// The constraint mirrors React.lazy's own: anything narrower rejects class components that declare
// defaultProps, such as react-datepicker's.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyChunk = <T extends ComponentType<any>>(load: () => Promise<{ default: T }>) =>
    lazy(() =>
        load().catch((error) => {
            throw new ChunkLoadError(error);
        }),
    );

export class ChunkLoadErrorBoundary extends Component<Props, State> {
    state: State = { error: null, resetKey: this.props.resetKey };

    static getDerivedStateFromError(error: unknown) {
        return { error };
    }

    static getDerivedStateFromProps(props: Props, state: State): State | null {
        if (props.resetKey === state.resetKey) {
            return null;
        }
        return { error: null, resetKey: props.resetKey };
    }

    private isChunkLoadError(error: unknown) {
        return error instanceof ChunkLoadError;
    }

    componentDidCatch(error: unknown) {
        const isChunkLoadError = this.isChunkLoadError(error);
        if (isChunkLoadError && this.props?.onError) {
            this.props.onError();
        }
        if (isChunkLoadError) {
            showErrorToast(i18next.t("common:error.failedToLoadFeature"));
        }
    }

    render() {
        if (this.isChunkLoadError(this.state.error)) {
            return this.props.fallback ?? null;
        }
        if (this.state.error) {
            throw this.state.error;
        }
        return this.props.children;
    }
}
